import JSDB from 'fromKotlin/nk';
import {massSendFilesOf} from 'api/customer/documents.api';
import {buildPlainDocWithPaymentWaMessage, DOC_OPEN_INVOICES} from './whatsappPatterns';

export type PayableDocItem = {
    clientId: number;
    doc: any;
    amount: number;
};

export type PayableClientGroup = {
    clientId: number;
    docs: any[];
    amount: number;
};

export function roundPayAmount(n: number): number {
    return Math.round(n * 100) / 100;
}

function isClosedInvoiceStatus(status: unknown): boolean {
    return !!status && status !== 'open' && status !== 'partly_closed';
}

/** Open amount on an invoice / delivery note. Canceled docs are 0. */
export function payableDocOpenAmount(doc: any): number {
    if (!doc || doc.status === 'canceled') return 0;
    const type = String(doc.type || '');
    const isInvoice = type === 'invoice';
    const isNote = type === 'delivery_certificate';
    if (!isInvoice && !isNote) return 0;
    if (isInvoice && isClosedInvoiceStatus(doc.status)) return 0;
    if (isNote && (Number(doc.paid) === 1 || Number(doc.nkObject?.paid) === 1 || Number(doc.header?.paid) === 1)) {
        return 0;
    }
    const nk = doc.nkObject;
    const invoiceLeft = [
        doc.value_left, nk?.value_left, doc.header?.valueLeft, nk?.valueLeft, doc.open_tn,
    ];
    const invoiceFallback = (doc.status === 'open' || doc.status === 'partly_closed')
        ? [doc.mainValue, nk?.totalValue, doc.header?.totalValue]
        : [];
    const candidates = isInvoice
        ? [...invoiceLeft, ...invoiceFallback]
        : [doc.mainValue, nk?.value, doc.header?.totalValue, nk?.totalValue];
    for (const raw of candidates) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return roundPayAmount(n);
    }
    return 0;
}

function docClientId(doc: any, fallbackClientId?: number): number {
    return Number(
        fallbackClientId
        ?? doc?.entId
        ?? doc?.client_id
        ?? doc?.nkObject?.ent_id
        ?? doc?.nkObject?.client_id
        ?? 0,
    );
}

export function collectPayableDocsFromDocuments(docs: any[]): PayableDocItem[] {
    const out: PayableDocItem[] = [];
    (docs || []).forEach((doc) => {
        if (!doc) return;
        const amount = payableDocOpenAmount(doc);
        const clientId = docClientId(doc);
        if (!(clientId > 0) || amount <= 0) return;
        out.push({clientId, doc, amount});
    });
    return out;
}

/** Carteset rows: prefer the open leftover on the row, then the linked invoice/note. */
export function collectPayableFromCartesetRows(rows: any[], fallbackClientId?: number): PayableDocItem[] {
    const out: PayableDocItem[] = [];
    (rows || []).forEach((row) => {
        const doc = row?.doc;
        if (!doc) return;
        const type = String(doc.type || row.type || '');
        const status = row.status || doc.status;
        const merged = {...doc, type, status, open_tn: row.open_tn ?? doc.open_tn};
        const fromRow = Number(row.open_tn);
        const amount = type === 'invoice' && Number.isFinite(fromRow)
            ? (fromRow > 0 ? roundPayAmount(fromRow) : 0)
            : payableDocOpenAmount(merged);
        const clientId = docClientId(doc, fallbackClientId ?? Number(row.entId));
        if (!(clientId > 0) || amount <= 0) return;
        out.push({clientId, doc: merged, amount});
    });
    return out;
}

/** Datasheet rows: prefer open invoices on the row, else unpaid delivery notes. */
export function collectPayableOrderDocs(rows: any[]): PayableDocItem[] {
    const out: PayableDocItem[] = [];
    (rows || []).forEach((row) => {
        const dvs = (row?.dvs || []).filter((d: any) => d && d.status !== 'canceled' && d.id != null);
        if (!dvs.length) return;
        const invoices = dvs.filter((d: any) => d.type === 'invoice');
        const notes = dvs.filter((d: any) => d.type === 'delivery_certificate');
        const chosen = invoices.some((d: any) => payableDocOpenAmount(d) > 0) ? invoices : notes;
        chosen.forEach((doc: any) => {
            const amount = payableDocOpenAmount(doc);
            if (amount > 0) out.push({clientId: Number(row.id), doc, amount});
        });
    });
    return out;
}

export function groupPayableByClient(items: PayableDocItem[]): PayableClientGroup[] {
    const map = new Map<number, PayableClientGroup>();
    const seen = new Set<string>();
    items.forEach((item) => {
        const docId = item.doc?.id ?? item.doc?.delivery_id ?? item.doc?.order_id;
        const docType = item.doc?.type ?? item.doc?.nkObject?.getConnectedDocType?.() ?? '';
        if (docId != null) {
            const dedupeKey = `${item.clientId}:${docType}:${docId}`;
            if (seen.has(dedupeKey)) return;
            seen.add(dedupeKey);
        }
        const cur = map.get(item.clientId) || {clientId: item.clientId, docs: [], amount: 0};
        cur.docs.push(item.doc);
        cur.amount = roundPayAmount(cur.amount + item.amount);
        map.set(item.clientId, cur);
    });
    return [...map.values()].filter((g) => g.amount > 0 && g.docs.length);
}

export function clientPaymentFields(clientId: number): {
    contact?: string;
    company?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
} {
    try {
        const client = JSDB().getClient(Number(clientId)).first;
        if (!client) return {};
        const street = [client.street, client.streetNumber ?? client.street_number].filter(Boolean).join(' ').trim()
            || (typeof client.getAddressStr === 'function' ? client.getAddressStr() : '');
        const emailRaw = typeof client.getEmail === 'function' ? client.getEmail() : client.email;
        const email = String(emailRaw || '').split(',')[0]?.trim();
        const phone = typeof client.getPhone === 'function' ? client.getPhone() : client.phone;
        return {
            contact: client.name,
            company: client.name,
            email: email || undefined,
            phone: phone || undefined,
            address: street || undefined,
            city: client.city || undefined,
        };
    } catch {
        return {};
    }
}

/** Flatten grouped clients into send-message parallel arrays (amount repeated per doc). */
export function paymentSendPayload(groups: PayableClientGroup[]): {
    selected: number[];
    files: {ids: number[]; types: number[]};
    externalVar: string[];
    msg: string;
    subject: string;
    defaultPlatform: 'whatsapp';
} {
    const selected: number[] = [];
    const docs: any[] = [];
    const amounts: string[] = [];
    groups.forEach((g) => {
        const amount = roundPayAmount(g.amount).toFixed(2);
        g.docs.forEach((doc) => {
            selected.push(g.clientId);
            docs.push(doc);
            amounts.push(amount);
        });
    });
    return {
        selected,
        files: massSendFilesOf(docs),
        externalVar: amounts,
        msg: buildPlainDocWithPaymentWaMessage(),
        subject: 'תשלום בלינק',
        defaultPlatform: 'whatsapp',
    };
}

export type PaymentSendPayload = ReturnType<typeof paymentSendPayload>;

/** Open-invoices PDF (doc type 21) + `@payment=#var` amount, one row per client. */
export function paymentSendPayloadForOpenInvoices(
    clients: {clientId: number; amount: number}[],
): PaymentSendPayload {
    const selected: number[] = [];
    const ids: number[] = [];
    const types: number[] = [];
    const amounts: string[] = [];
    clients.forEach((c) => {
        const amount = roundPayAmount(c.amount);
        if (!(c.clientId > 0) || !(amount > 0)) return;
        selected.push(c.clientId);
        ids.push(c.clientId);
        types.push(DOC_OPEN_INVOICES);
        amounts.push(amount.toFixed(2));
    });
    return {
        selected,
        files: {ids, types},
        externalVar: amounts,
        msg: buildPlainDocWithPaymentWaMessage(),
        subject: 'תשלום בלינק',
        defaultPlatform: 'whatsapp',
    };
}

export function paymentSendFromDocs(docs: any[]): PaymentSendPayload {
    return paymentSendPayload(groupPayableByClient(collectPayableDocsFromDocuments(docs)));
}
